import { Trans } from "react-i18next";
import React from "react";
import { Badge } from "react-bootstrap";
import "./SignalStateStrip.css";

interface SignalStateStripProps {
  patientId: string;
  studyId: string;
  isClinicalMode: boolean;
  perspectiveName: string;
  perspectiveVersion?: string;
  montageName: string;
  referenceType: string;
  channelCount: number;
  highPassFilter: number;
  lowPassFilter: number;
  notchFilter: boolean;
  notchFilter50hz?: boolean;
  polarity?: "negative-up" | "positive-up";
  /** Sensitivity in µV/mm (ACNS). Prop name kept as `gain` for compatibility. */
  gain: number;
  autoScale: boolean;
  timebase: number;
  /** Derived paper speed; omit unless measured calibration passed. */
  horizontalMmPerSec?: number | null;
  pyramidLevel: number;
  effectiveSamplingRate: number;
  /** When true, omit patientId/studyId (header already shows identity). Default false. */
  hidePatientStudy?: boolean;
  /** Single-row ≤28px layout for always-on EEGViewer chrome. Default false. */
  compact?: boolean;
  /** Study classification badge (ROUTINE / LTM / ICU / etc.). */
  studyType?: string | null;
  montageLocked?: boolean;
  filtersLocked?: boolean;
  onQuickSwitch?: () => void;
  hasUnappliedChanges?: boolean;
  children?: React.ReactNode;
}

function formatNotchSummary(
  notchFilter: boolean,
  notchFilter50hz?: boolean,
): string {
  if (!notchFilter) return "Notch Off";
  return notchFilter50hz ? "Notch 50Hz" : "Notch 60Hz";
}

function formatFilterSummary(
  highPassFilter: number,
  lowPassFilter: number,
  notchFilter: boolean,
  notchFilter50hz?: boolean,
): string {
  const hp = highPassFilter > 0 ? `HP ${highPassFilter}` : "HP Off";
  const lp = lowPassFilter > 0 ? `LP ${lowPassFilter}` : "LP Off";
  const notch = formatNotchSummary(notchFilter, notchFilter50hz);
  return `${hp} · ${lp} · ${notch}`;
}

export const SignalStateStrip: React.FC<SignalStateStripProps> = ({
  patientId,
  studyId,
  isClinicalMode,
  perspectiveName,
  perspectiveVersion,
  montageName,
  referenceType,
  channelCount,
  highPassFilter,
  lowPassFilter,
  notchFilter,
  notchFilter50hz,
  gain,
  autoScale,
  timebase,
  horizontalMmPerSec = null,
  polarity = "negative-up",
  pyramidLevel,
  effectiveSamplingRate,
  hidePatientStudy = false,
  compact = false,
  studyType = null,
  montageLocked = false,
  filtersLocked = false,
  hasUnappliedChanges = false,
  children,
}) => {
  const stripClass = [
    "signal-state-strip",
    isClinicalMode
      ? "signal-state-strip--clinical"
      : "signal-state-strip--research",
    compact ? "signal-state-strip--compact" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const studyTypeLabel =
    studyType && studyType.trim().length > 0 ? studyType.trim() : null;
  const showStudyTypePlaceholder = compact && !studyTypeLabel;

  const modeBadge = (
    <Badge
      bg={isClinicalMode ? "danger" : "success"}
      className="signal-state-strip__badge"
      aria-label={`Current mode: ${isClinicalMode ? "Clinical - restricted editing" : "Research - full editing"}`}
    >
      <span
        className="signal-state-strip__mode-indicator"
        aria-hidden="true"
      />
      {isClinicalMode ? "CLINICAL" : "RESEARCH"}
      {!compact ? " MODE" : ""}
    </Badge>
  );

  const draftBadge = hasUnappliedChanges ? (
    <Badge
      bg="warning"
      className="signal-state-strip__draft-badge"
      role="alert"
      aria-live="assertive"
    >
      <Trans ns="features" i18nKey="ui.copy.539516e7658f" />
    </Badge>
  ) : null;

  if (compact) {
    return (
      <div className={stripClass} role="status" aria-live="polite">
        <div className="signal-state-strip__row signal-state-strip__row--compact">
          {modeBadge}

          <div
            className="signal-state-strip__param"
            title={montageLocked ? "Montage locked" : "Montage unlocked"}
          >
            <span className="signal-state-strip__param-label" aria-hidden="true">
              {montageLocked ? "🔒" : ""}
            </span>
            <span>{montageName || "—"}</span>
          </div>

          <div
            className="signal-state-strip__param"
            title={filtersLocked ? "Filters locked" : "Filters unlocked"}
          >
            <span className="signal-state-strip__param-label" aria-hidden="true">
              {filtersLocked ? "🔒" : ""}
            </span>
            <span>
              {formatFilterSummary(
                highPassFilter,
                lowPassFilter,
                notchFilter,
                notchFilter50hz,
              )}
            </span>
          </div>

          <div
            className="signal-state-strip__param"
            title="Sensitivity (µV of input per millimetre of trace deflection)"
          >
            <span>
              Sensitivity {gain}
              <Trans ns="features" i18nKey="ui.copy.73dc899cce25" />
              {autoScale ? " (Auto)" : ""}
            </span>
          </div>

          <div
            className="signal-state-strip__param"
            title="Seconds of EEG shown across the current page (user-selectable)"
          >
            <span>
              {timebase} s/page
            </span>
          </div>

          {typeof horizontalMmPerSec === "number" &&
          Number.isFinite(horizontalMmPerSec) ? (
            <div
              className="signal-state-strip__param"
              title="Derived paper speed from measured screen calibration"
            >
              <span>{horizontalMmPerSec.toFixed(1)} mm/s</span>
            </div>
          ) : null}

          {studyTypeLabel ? (
            <Badge
              bg="secondary"
              className="signal-state-strip__study-type"
              title="Study type"
            >
              {studyTypeLabel}
            </Badge>
          ) : showStudyTypePlaceholder ? (
            <span className="signal-state-strip__placeholder">No study loaded</span>
          ) : null}

          {perspectiveName ? (
            <div className="signal-state-strip__param">
              <span className="signal-state-strip__param-label">
                <Trans ns="features" i18nKey="ui.copy.9874f7ee30fc" />
              </span>
              <span>
                {perspectiveName}
                {perspectiveVersion ? ` v${perspectiveVersion}` : ""}
              </span>
            </div>
          ) : null}

          {draftBadge}

          {children ? (
            <div className="signal-state-strip__controls">{children}</div>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className={stripClass} role="status" aria-live="polite">
      {/* Row 1: Mode, Perspective */}
      <div className="signal-state-strip__row">
        {modeBadge}

        <div className="signal-state-strip__param">
          <span className="signal-state-strip__param-label">
            <Trans ns="features" i18nKey="ui.copy.9874f7ee30fc" />
          </span>
          <span>
            {perspectiveName} {perspectiveVersion && `v${perspectiveVersion}`}
          </span>
        </div>

        {draftBadge}

        {children && (
          <div className="signal-state-strip__controls">{children}</div>
        )}
      </div>

      {/* Row 2: Patient, study, montage metadata */}
      <div className="signal-state-strip__row">
        {!hidePatientStudy && (
          <>
            <div className="signal-state-strip__param">
              <span className="signal-state-strip__param-label">
                <Trans ns="features" i18nKey="ui.copy.6da1f15b81b6" />
              </span>
              <span>{patientId}</span>
            </div>
            <div className="signal-state-strip__param">
              <span className="signal-state-strip__param-label">
                <Trans ns="features" i18nKey="ui.copy.e8ffee39e14d" />
              </span>
              <span>{studyId}</span>
            </div>
          </>
        )}
        <div className="signal-state-strip__param">
          <span>{montageName}</span>
        </div>
        <div className="signal-state-strip__param">
          <span>
            {channelCount}
            <Trans ns="features" i18nKey="ui.copy.482bd64c6c9f" />
          </span>
        </div>
        <div className="signal-state-strip__param">
          <span>{referenceType}</span>
        </div>
      </div>

      {/* Row 3: Filters and display settings */}
      <div className="signal-state-strip__row">
        <div className="signal-state-strip__param">
          <span>
            {highPassFilter > 0 ? `HP ${highPassFilter}Hz` : "HP Off"}
          </span>
        </div>
        <div className="signal-state-strip__param">
          <span>{lowPassFilter > 0 ? `LP ${lowPassFilter}Hz` : "LP Off"}</span>
        </div>
        <div className="signal-state-strip__param">
          <span>{formatNotchSummary(notchFilter, notchFilter50hz)}</span>
        </div>
        <div
          className="signal-state-strip__param"
          title="Sensitivity (µV of input per millimetre of trace deflection)"
        >
          <span>
            Sensitivity {gain}
            <Trans ns="features" i18nKey="ui.copy.73dc899cce25" />
            {autoScale ? " (Auto)" : ""}
          </span>
        </div>
        <div className="signal-state-strip__param">
          <span>
            {timebase} s/page
          </span>
        </div>
        {typeof horizontalMmPerSec === "number" &&
        Number.isFinite(horizontalMmPerSec) ? (
          <div className="signal-state-strip__param">
            <span>{horizontalMmPerSec.toFixed(1)} mm/s</span>
          </div>
        ) : null}
      </div>

      {/* Row 4: Safety/diagnostic metadata (non-duplicative with visible controls) */}
      <div className="signal-state-strip__row">
        {polarity === "positive-up" && (
          <>
            <span
              className="signal-state-strip__polarity-warning"
              title="Positive-up polarity is non-standard in clinical EEG review"
            >
              <Trans ns="features" i18nKey="ui.copy.da2ade118fe8" />
            </span>
            <div className="signal-state-strip__separator">|</div>
          </>
        )}

        <div className="signal-state-strip__param">
          <span className="signal-state-strip__param-label">
            <Trans ns="features" i18nKey="ui.copy.222707d26905" />
          </span>
          <span>
            <Trans ns="features" i18nKey="ui.copy.7c7f5d049fad" />{" "}
            {pyramidLevel} ({effectiveSamplingRate}
            <Trans ns="features" i18nKey="ui.copy.4b3dfe314116" />
          </span>
        </div>
      </div>
    </div>
  );
};

export default SignalStateStrip;
